/**
 * O seletor de provedor de autenticação do portal.
 *
 * `VITE_AUTH_PROVIDER=cognito|supabase` escolhe a implementação por trás desta
 * porta. A migração mantém o Cognito por um release (rollback por variável); o
 * default é `cognito` para que um build sem a variável não troque de provedor
 * sozinho.
 *
 * Nenhum dos SDKs entra no bundle de entrada: o do Cognito é importado sob
 * demanda por `./cognito-sdk`; o do Supabase, por `./supabase`.
 */
import {
  completeBlogSignIn,
  isGoogleSignInEnabled as cognitoGoogleEnabled,
  startBlogSignOut,
  startGoogleSignIn,
} from './federated';
import {
  blogUserFromIdToken,
  getBlogIdToken,
  sessionFromTokens,
  storeSession,
  type BlogMember,
} from './session';
import type { EmailLinkType } from './types';

type Provider = 'cognito' | 'supabase';

function provider(): Provider {
  return import.meta.env.VITE_AUTH_PROVIDER === 'supabase' ? 'supabase' : 'cognito';
}

function cognitoConfigured(): boolean {
  const userPoolId = (import.meta.env.VITE_PORTAL_COGNITO_USER_POOL_ID ?? '').trim();
  const clientId = (import.meta.env.VITE_PORTAL_COGNITO_CLIENT_ID ?? '').trim();
  return Boolean(clientId) && /^[\w-]+_[0-9A-Za-z]+$/.test(userPoolId);
}

function supabaseConfigured(): boolean {
  const url = (import.meta.env.VITE_SUPABASE_URL ?? '').trim();
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();
  return url.startsWith('https://') && Boolean(anonKey);
}

/** True quando o formulário de e-mail e senha deve aparecer. */
export function isPasswordSignInEnabled(): boolean {
  return provider() === 'supabase' ? supabaseConfigured() : cognitoConfigured();
}

/** True quando o botão do Google deve aparecer. */
export function isGoogleSignInEnabled(): boolean {
  if (import.meta.env.VITE_PORTAL_GOOGLE_ENABLED !== 'true') return false;
  return provider() === 'supabase' ? supabaseConfigured() : cognitoGoogleEnabled();
}

/**
 * True quando há uma sessão do Supabase guardada na aba. Evita baixar o SDK
 * (~100 kB) no primeiro carregamento do blog para quem é visitante anônimo.
 */
function hasSupabaseSession(): boolean {
  try {
    return Object.keys(sessionStorage).some(
      (key) => key.startsWith('sb-') && key.includes('-auth-token'),
    );
  } catch {
    return false;
  }
}

/** O membro restaurado da sessão da aba (ou null). */
export async function restoreMember(): Promise<BlogMember | null> {
  if (provider() === 'supabase') {
    if (!hasSupabaseSession()) return null;
    const { restoreSession } = await import('./supabase');
    return restoreSession();
  }
  const token = await getBlogIdToken();
  return token ? blogUserFromIdToken(token) : null;
}

/** Token fresco para o Bearer da Members API. */
export async function getAccessToken(): Promise<string | null> {
  if (provider() === 'supabase') {
    const { getAccessToken: current } = await import('./supabase');
    return current();
  }
  return getBlogIdToken();
}

export async function signInWithPassword(email: string, password: string): Promise<BlogMember> {
  if (provider() === 'supabase') {
    const { signInWithPassword: authenticate } = await import('./supabase');
    return authenticate(email, password);
  }
  const { signInWithPassword: authenticate } = await import('./cognito-sdk');
  const session = sessionFromTokens(await authenticate(email, password), false);
  const member = session ? blogUserFromIdToken(session.idToken) : null;
  if (!session || !member) {
    const { PortalAuthError } = await import('./cognito-errors');
    throw new PortalAuthError(
      'IncompleteSession',
      'A resposta do servidor de autenticação veio incompleta. Tente novamente.',
    );
  }
  storeSession(session);
  return member;
}

/** Resolve `true` quando a conta já nasce com sessão (confirmação desligada). */
export async function signUp(input: {
  email: string;
  password: string;
  name: string;
}): Promise<boolean> {
  if (provider() === 'supabase') {
    const { signUp: create } = await import('./supabase');
    return create(input);
  }
  const { signUp: create } = await import('./cognito-sdk');
  return create(input);
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  if (provider() === 'supabase') {
    const { confirmSignUp: confirm } = await import('./supabase');
    await confirm(email, code);
    return;
  }
  const { confirmSignUp: confirm } = await import('./cognito-sdk');
  await confirm(email, code);
}

export async function resendSignUpCode(email: string): Promise<void> {
  if (provider() === 'supabase') {
    const { resendSignUpCode: resend } = await import('./supabase');
    await resend(email);
    return;
  }
  const { resendSignUpCode: resend } = await import('./cognito-sdk');
  await resend(email);
}

export type PasswordResetDelivery = 'link' | 'code';

/**
 * Como a redefinição de senha chega: o Supabase manda o link, que abre direto
 * na senha nova em /auth/confirm; o Cognito do rollback só sabe mandar código.
 */
export function passwordResetDelivery(): PasswordResetDelivery {
  return provider() === 'supabase' ? 'link' : 'code';
}

export async function requestPasswordReset(email: string): Promise<void> {
  if (provider() === 'supabase') {
    const { requestPasswordReset: request } = await import('./supabase');
    await request(email);
    return;
  }
  const { requestPasswordReset: request } = await import('./cognito-sdk');
  await request(email);
}

/** A redefinição por código, só do Cognito: no Supabase ela é pelo link do e-mail. */
export async function confirmPasswordReset(
  email: string,
  code: string,
  password: string,
): Promise<void> {
  if (provider() === 'supabase') {
    const { PortalAuthError } = await import('./cognito-errors');
    throw new PortalAuthError(
      'ResetCodeUnavailable',
      'A redefinição por código não existe neste login; use o link do e-mail.',
    );
  }
  const { confirmPasswordReset: confirm } = await import('./cognito-sdk');
  await confirm(email, code, password);
}

/** Só o Supabase manda link por e-mail: com o Cognito, a tela explica e volta ao /login. */
async function emailLinkUnavailable(): Promise<never> {
  const { PortalAuthError } = await import('./cognito-errors');
  throw new PortalAuthError(
    'LinkUnavailable',
    'Este link é do novo login do portal, que ainda não está no ar. Entre pela página de login.',
  );
}

/**
 * Confirma o link de um e-mail do Auth (cadastro, convite, senha, acesso, troca
 * de e-mail) e devolve o e-mail da conta que entrou.
 */
export async function confirmEmailLink(tokenHash: string, type: EmailLinkType): Promise<string | null> {
  if (provider() !== 'supabase') return emailLinkUnavailable();
  const { confirmEmailLink: confirm } = await import('./supabase');
  return confirm(tokenHash, type);
}

/**
 * Troca o nome de exibição. Só o Supabase: o pool do Cognito de rollback não
 * tem a tela, e a troca de nome fica indisponível até o release seguinte.
 */
export async function updateDisplayName(name: string): Promise<BlogMember> {
  if (provider() === 'supabase') {
    const { updateDisplayName: save } = await import('./supabase');
    return save(name);
  }
  const { PortalAuthError } = await import('./cognito-errors');
  throw new PortalAuthError(
    'ProfileUnavailable',
    'A troca de nome não está disponível neste login. Tente mais tarde.',
  );
}

/** Grava a senha da sessão aberta pelo link (convite ou redefinição). */
export async function setNewPassword(password: string): Promise<void> {
  if (provider() !== 'supabase') return emailLinkUnavailable();
  const { setNewPassword: save } = await import('./supabase');
  await save(password);
}

/** Sai da página para o consentimento do Google (Supabase OAuth ou Hosted UI). */
export async function signInWithGoogle(returnPath?: string): Promise<void> {
  if (provider() === 'supabase') {
    const { signInWithGoogle: start } = await import('./supabase');
    await start(returnPath);
    return;
  }
  await startGoogleSignIn({ returnPath });
}

export async function signOut(): Promise<void> {
  if (provider() === 'supabase') {
    const { signOut: end } = await import('./supabase');
    await end();
    return;
  }
  await startBlogSignOut();
}

/** Fecha o retorno do OAuth e devolve o caminho interno de volta. */
export async function completeOAuthCallback(): Promise<string> {
  if (provider() === 'supabase') {
    const { completeOAuthCallback: complete } = await import('./supabase');
    return complete();
  }
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) throw new Error('A resposta do login veio incompleta. Recomece o login.');
  const result = await completeBlogSignIn(code, state);
  return result.returnPath;
}
